import type { ProfileConfig } from "@worker/config-schema";
import { el } from "../lib/dom";
import { renderProfileParts } from "./parts";

/**
 * Centered stack: avatar + name + description centered at the top, meta row
 * beneath, widgets/socials/links stacked centrally, audio at the bottom.
 * Good for portfolio-style profiles where socials are the hero, not widgets.
 */
export function renderCenteredStack(
  config: ProfileConfig,
  options: { gateOpened: Promise<void> } = { gateOpened: Promise.resolve() },
): HTMLElement {
  const p = renderProfileParts(config, options);
  p.card.classList.add("card--centered");

  const header = el("header", { class: "card__header card__header--centered" }, [
    p.avatar,
    el("div", { class: "card__identity" }, [
      el("div", { class: "card__name-row card__name-row--centered" }, [p.username, p.badges]),
      p.description,
    ]),
  ]);

  // Centered-stack's aesthetic demands that views / location / clock sit
  // centered as a horizontal pill and the contact button floats in the
  // top-right corner (never inline). If the user explicitly placed
  // contact elsewhere via metaPlacement we respect that; otherwise we
  // force it into the top-right corner automatically.
  const metaRow = el("div", { class: "card__meta card__meta--centered" });
  const vc = p.meta.getInline("views");    if (vc) metaRow.append(vc);
  const loc = p.meta.getInline("location"); if (loc) metaRow.append(loc);
  const clk = p.meta.getInline("clock");    if (clk) metaRow.append(clk);
  const ct = p.meta.getInline("contact");
  if (ct && p.contact) {
    // Contact came through getInline (i.e. user placement is "inline").
    // Pull it out of the meta row and route to top-right corner instead.
    const corner = el("div", { class: "card__corner card__corner--top-right" }, [ct]);
    p.card.append(corner);
  }

  p.card.append(header);
  if (metaRow.childElementCount > 0) p.card.append(metaRow);
  if (p.socials) p.card.append(wrapSlot("socials", p.socials));
  if (p.widgets) p.card.append(wrapSlot("widgets", p.widgets));
  if (p.links) p.card.append(wrapSlot("links", p.links));
  if (p.audio) p.card.append(wrapSlot("audio", p.audio));
  p.meta.applyCornerZones();

  return p.card;
}

function wrapSlot(name: string, child: HTMLElement): HTMLElement {
  return el("div", { class: `card__slot card__slot--${name}`, "data-slot": name }, [child]);
}
